import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useCart } from "../lib/cart";
import { api } from "../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { ShoppingBag, Trash2, Mail } from "lucide-react";
import { toast } from "sonner";

export default function CartDrawer() {
  const cart = useCart();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [saved, setSaved] = useState(false);

  async function saveBag() {
    if (!email) return;
    try {
      await api.post("/cart/abandoned", { email, item_count: cart.count });
      setSaved(true);
      toast.success("Saved! We'll send a 10% off code if you don't check out in 24h.");
    } catch {
      toast.error("Couldn't save bag.");
    }
  }

  return (
    <Sheet open={cart.open} onOpenChange={cart.setOpen}>
      <SheetContent
        data-testid="cart-drawer"
        side="right"
        className="bg-[#FDFBF7] border-l border-[#EAE4D3] w-full sm:max-w-md p-0 flex flex-col"
      >
        <SheetHeader className="p-6 border-b border-[#EAE4D3]">
          <SheetTitle className="font-serif text-2xl flex items-center justify-between">
            <span>Your bag</span>
            <span className="text-sm text-[#5C5547] font-sans">{cart.count} item{cart.count !== 1 ? "s" : ""}</span>
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6">
          {cart.items.length === 0 ? (
            <div className="text-center py-20" data-testid="cart-empty">
              <div className="w-14 h-14 mx-auto rounded-full bg-[#F5F2EA] flex items-center justify-center">
                <ShoppingBag className="w-6 h-6 text-[#5C5547]" />
              </div>
              <div className="mt-4 font-serif text-2xl">Your bag is empty.</div>
              <div className="mt-2 text-sm text-[#5C5547]">Discover something beautiful.</div>
              <Link to="/shop" onClick={() => cart.setOpen(false)} data-testid="cart-shop-link"
                className="mt-6 inline-block px-6 py-2.5 rounded-full bg-[#2C2924] text-[#FDFBF7] text-sm">
                Browse the shop
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {cart.items.map((it) => (
                <div key={it.cartKey || it.id} data-testid={`cart-item-${it.cartKey || it.id}`}
                  className="flex gap-3 p-3 rounded-2xl border border-[#EAE4D3] bg-[#FDFBF7]">
                  {it.image_url && (
                    <img src={it.image_url} alt={it.title}
                      className="w-20 h-20 rounded-xl object-cover shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm line-clamp-2">{it.title}</div>
                    <div className="text-xs text-[#5C5547] mt-0.5">{it.category}</div>
                    {it.variant_name && (
                      <div className="text-xs text-[#2C2924] mt-1" data-testid={`cart-variant-${it.cartKey}`}>{it.variant_name}</div>
                    )}
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <div className="font-serif text-base">{it.price > 0 ? `$${it.price.toFixed(2)}` : "Free"}</div>
                      <div className="flex gap-1">
                        <button onClick={() => { cart.setOpen(false); navigate(`/product/${it.id}`); }}
                          data-testid={`cart-checkout-${it.id}`}
                          className="px-3 py-1.5 rounded-full bg-[#2C2924] text-[#FDFBF7] text-xs">
                          Checkout
                        </button>
                        <button onClick={() => cart.remove(it.cartKey || it.id)} data-testid={`cart-remove-${it.cartKey || it.id}`}
                          className="p-1.5 rounded-full hover:bg-red-50 text-red-600">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {cart.items.length > 0 && (
          <div className="p-6 border-t border-[#EAE4D3] bg-[#F5F2EA]">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-[#5C5547]">Subtotal</span>
              <span className="font-serif text-2xl">${cart.total.toFixed(2)}</span>
            </div>
            <p className="text-xs text-[#5C5547] mb-4">
              Each item is checked out individually so you can pay with your preferred method.
            </p>

            {!saved ? (
              <div className="mb-4 p-3 rounded-xl bg-white border border-[#EAE4D3]" data-testid="cart-save-block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5547] mb-2 inline-flex items-center gap-1">
                  <Mail className="w-3 h-3" /> Save your bag — get 10% off if you come back
                </div>
                <div className="flex gap-2">
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    data-testid="cart-save-email"
                    className="flex-1 px-3 py-2 rounded-full bg-[#F5F2EA] border border-[#EAE4D3] text-xs" />
                  <button onClick={saveBag} disabled={!email} data-testid="cart-save-btn"
                    className="px-4 py-2 rounded-full text-xs font-semibold disabled:opacity-60"
                    style={{ background: "var(--accent-strong)", color: "#000" }}>
                    Save bag
                  </button>
                </div>
              </div>
            ) : (
              <div className="mb-4 p-3 rounded-xl bg-[#1F6B52]/10 border border-[#1F6B52]/30 text-xs text-[#0F3D2E]" data-testid="cart-saved">
                Saved! We'll email you a 10% off code in 24h if you don't check out.
              </div>
            )}

            <button onClick={() => cart.clear()} data-testid="cart-clear"
              className="w-full py-2.5 rounded-full bg-[#FDFBF7] border border-[#EAE4D3] text-sm hover:border-red-200 hover:text-red-600">
              Clear bag
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
